import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mapResponseError, type MappedApiError } from '@mybusinessmate/api-client';
import type {
  AgencyProfileInput,
  ChangeStamp,
  FeatureFlagInput,
  FeatureFlagRecord,
  LimitRules,
  MaintenanceCommand,
  MaintenanceState,
  ResetTwoFactorInput,
  SchedulingRules,
  SecurityPolicy,
  SecurityPolicyResponse,
  StaffPreferences,
  StaffPreferenceSection,
  TeamNotificationRules,
  AgencyProfile,
} from '@mybusinessmate/schema';
import { useToast } from '../components/ui/toast';
import { api } from './api';

/** The server's answer for a failed call as a message the screen can show. */
const failure = (e: unknown) => (e as Partial<MappedApiError>)?.message ?? 'Something went wrong. Please try again.';

type WithChange<T> = T & { changed?: ChangeStamp };

// --- Workspace sections (admin only) ---------------------------------------------------------------------------

interface SectionDef<T, I> {
  key: string;
  load: () => Promise<T>;
  save: (input: I) => Promise<T>;
}

/** Calls `fn`, turns a non-2xx answer into a mapped error, and returns the JSON body. */
async function call<T>(fn: () => Promise<Response>): Promise<T> {
  const res = await fn();
  if (!res.ok) throw await mapResponseError(res);
  return (await res.json()) as T;
}

const SECTIONS = {
  agency: {
    key: 'agency',
    load: () => call<WithChange<AgencyProfile>>(() => api.api.admin.settings.agency.$get()),
    save: (json: AgencyProfileInput) =>
      call<WithChange<AgencyProfile>>(() => api.api.admin.settings.agency.$put({ json })),
  },
  securityPolicy: {
    key: 'security-policy',
    load: () => call<SecurityPolicyResponse>(() => api.api.admin.settings['security-policy'].$get()),
    save: (json: SecurityPolicy) =>
      call<SecurityPolicyResponse>(() => api.api.admin.settings['security-policy'].$put({ json })),
  },
  scheduling: {
    key: 'scheduling',
    load: () => call<WithChange<SchedulingRules>>(() => api.api.admin.settings.scheduling.$get()),
    save: (json: SchedulingRules) =>
      call<WithChange<SchedulingRules>>(() => api.api.admin.settings.scheduling.$put({ json })),
  },
  teamNotifications: {
    key: 'team-notifications',
    load: () => call<WithChange<TeamNotificationRules>>(() => api.api.admin.settings['team-notifications'].$get()),
    save: (json: TeamNotificationRules) =>
      call<WithChange<TeamNotificationRules>>(() => api.api.admin.settings['team-notifications'].$put({ json })),
  },
  limits: {
    key: 'limits',
    load: () => call<WithChange<LimitRules>>(() => api.api.admin.settings.limits.$get()),
    save: (json: LimitRules) => call<WithChange<LimitRules>>(() => api.api.admin.settings.limits.$put({ json })),
  },
};

export type SectionName = keyof typeof SECTIONS;
type SectionData<N extends SectionName> = Awaited<ReturnType<(typeof SECTIONS)[N]['load']>>;
type SectionInput<N extends SectionName> = Parameters<(typeof SECTIONS)[N]['save']>[0];

const sectionKey = (name: SectionName) => ['workspace', SECTIONS[name].key] as const;

/**
 * One workspace section: the saved values and a `save` that updates them optimistically (a toggle should not wait
 * for the round trip) and rolls back with a message if the server refuses.
 */
export function useWorkspaceSection<N extends SectionName>(name: N) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const def = SECTIONS[name] as unknown as SectionDef<SectionData<N>, SectionInput<N>>;
  const key = sectionKey(name);

  const query = useQuery({ queryKey: key, queryFn: def.load, staleTime: 30_000 });
  const save = useMutation({
    mutationFn: def.save,
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SectionData<N>>(key);
      if (previous) queryClient.setQueryData(key, { ...previous, ...input });
      return { previous };
    },
    onError: (error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      toast({ title: 'Couldn’t save that', description: failure(error), tone: 'error' });
    },
    onSuccess: (saved) => queryClient.setQueryData(key, saved),
  });
  return { ...query, save };
}

/**
 * A section for value-row pages: `patch({ taskFileMb: 300 })` saves the whole section with that change on top, so
 * the page never has to know about the server-only fields (`changed`, `requireTwoFactorSince`).
 */
export function useSectionPatch<N extends SectionName>(name: N) {
  const section = useWorkspaceSection(name);
  const patch = (changes: Partial<SectionInput<N>>) => {
    if (!section.data) return;
    const {
      changed: _changed,
      requireTwoFactorSince: _since,
      ...current
    } = section.data as SectionData<N> & {
      requireTwoFactorSince?: string;
    };
    section.save.mutate({ ...current, ...changes } as unknown as SectionInput<N>);
  };
  return { data: section.data, isLoading: section.isLoading, isSaving: section.save.isPending, patch };
}

// --- Maintenance mode ------------------------------------------------------------------------------------------

export const maintenanceQuery = queryOptions({
  queryKey: ['workspace', 'maintenance'] as const,
  queryFn: () => call<MaintenanceState>(() => api.api.admin.maintenance.$get()),
  staleTime: 15_000,
});

export function useMaintenance() {
  return useQuery({ ...maintenanceQuery, refetchInterval: 30_000 });
}

export function useMaintenanceCommand() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (json: MaintenanceCommand) => call<MaintenanceState>(() => api.api.admin.maintenance.$post({ json })),
    onSuccess: (state) => queryClient.setQueryData(maintenanceQuery.queryKey, state),
    onError: (error) =>
      toast({ title: 'Couldn’t change maintenance mode', description: failure(error), tone: 'error' }),
  });
}

// --- Feature flags ---------------------------------------------------------------------------------------------

export const flagsQuery = queryOptions({
  queryKey: ['workspace', 'flags'] as const,
  queryFn: async () => (await call<{ flags: FeatureFlagRecord[] }>(() => api.api.admin.flags.$get())).flags,
  staleTime: 15_000,
});

export function useSaveFlag() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ key, input }: { key: string; input: FeatureFlagInput }) =>
      call<FeatureFlagRecord>(() => api.api.admin.flags[':key'].$put({ param: { key }, json: input })),
    onSuccess: (flag) => {
      queryClient.setQueryData<FeatureFlagRecord[]>(flagsQuery.queryKey, (flags) =>
        flags?.map((f) => (f.key === flag.key ? flag : f)),
      );
      void queryClient.invalidateQueries({ queryKey: ['workspace', 'flags', flag.key, 'history'] });
    },
    onError: (error) => toast({ title: 'Couldn’t save the flag', description: failure(error), tone: 'error' }),
  });
}

export const flagHistoryQuery = (key: string) =>
  queryOptions({
    queryKey: ['workspace', 'flags', key, 'history'] as const,
    queryFn: async () =>
      (
        await call<{ history: { id: string; by: string; at: string; title: string; detail: string | null }[] }>(() =>
          api.api.admin.flags[':key'].history.$get({ param: { key } }),
        )
      ).history,
  });

// --- Staff sign-in health --------------------------------------------------------------------------------------

export interface StaffHealthRow {
  id: string;
  name: string;
  email: string;
  staffRole: 'admin' | 'account_manager' | 'lead_specialist' | 'specialist';
  twoFactorEnabled: boolean;
  lockedUntil: string | null;
  lastSignInAt: string | null;
  deviceCount: number;
}

export const staffHealthQuery = queryOptions({
  queryKey: ['staff', 'health'] as const,
  queryFn: async () => (await call<{ staff: StaffHealthRow[] }>(() => api.api.admin.staff.security.$get())).staff,
  staleTime: 15_000,
});

/** Throws the mapped error on failure so the dialog can show the server's reason. */
export const resetStaffTwoFactor = (id: string, json: ResetTwoFactorInput) =>
  call<{ status: true }>(() => api.api.admin.staff[':id']['reset-two-factor'].$post({ param: { id }, json }));

export type IntegrationStatus = 'healthy' | 'configured' | 'unconfigured' | 'error' | 'unknown';
export const integrationsQuery = queryOptions({
  queryKey: ['workspace', 'integrations'] as const,
  queryFn: async () =>
    (
      await call<{ integrations: { id: string; status: IntegrationStatus; detail?: string }[] }>(() =>
        api.api.admin.integrations.health.$get(),
      )
    ).integrations,
  staleTime: 30_000,
});

// --- The signed-in staff member --------------------------------------------------------------------------------

export interface StaffSecurity {
  twoFactorEnabled: boolean;
  twoFactorMandatory: boolean;
  twoFactorRequired: boolean;
  graceEndsAt?: string;
  mustSetUpNow: boolean;
  reauthMinutes: number;
  allowGoogleSignIn: boolean;
}

export const staffSecurityQuery = queryOptions({
  queryKey: ['staff', 'me', 'security'] as const,
  queryFn: () => call<StaffSecurity>(() => api.api.staff.me.security.$get()),
  staleTime: 30_000,
});

export function useStaffSecurity() {
  return useQuery(staffSecurityQuery);
}

export interface StaffRecord {
  id: string;
  name: string;
  email: string;
  staffRole: StaffHealthRow['staffRole'];
  department: string | null;
  title: string | null;
  skills: string[];
  maxActiveTasks: number;
}

export const staffMeQuery = queryOptions({
  queryKey: ['staff', 'me'] as const,
  queryFn: () => call<StaffRecord>(() => api.api.staff.me.$get()),
  staleTime: 60_000,
});

const prefsKey = ['staff', 'me', 'preferences'] as const;

const SAVE_PREFS: { [S in StaffPreferenceSection]: (json: StaffPreferences[S]) => Promise<StaffPreferences[S]> } = {
  profile: (json) => call(() => api.api.staff.me.preferences.profile.$put({ json })),
  notifications: (json) => call(() => api.api.staff.me.preferences.notifications.$put({ json })),
  availability: (json) => call(() => api.api.staff.me.preferences.availability.$put({ json })),
  connections: (json) => call(() => api.api.staff.me.preferences.connections.$put({ json })),
};

/** One section of the signed-in staff member's preferences, with an optimistic `save`. */
export function useStaffPreferences<S extends StaffPreferenceSection>(section: S) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: prefsKey,
    queryFn: () => call<StaffPreferences>(() => api.api.staff.me.preferences.$get()),
    staleTime: 60_000,
  });
  const save = useMutation({
    mutationFn: (value: StaffPreferences[S]) => SAVE_PREFS[section](value),
    onMutate: async (value) => {
      await queryClient.cancelQueries({ queryKey: prefsKey });
      const previous = queryClient.getQueryData<StaffPreferences>(prefsKey);
      if (previous) queryClient.setQueryData(prefsKey, { ...previous, [section]: value });
      return { previous };
    },
    onError: (error, _value, context) => {
      if (context?.previous) queryClient.setQueryData(prefsKey, context.previous);
      toast({ title: 'Couldn’t save that', description: failure(error), tone: 'error' });
    },
    onSuccess: (saved) =>
      queryClient.setQueryData<StaffPreferences>(prefsKey, (all) => (all ? { ...all, [section]: saved } : all)),
  });
  return { data: query.data?.[section], isLoading: query.isLoading, isError: query.isError, save };
}

// --- Public config ---------------------------------------------------------------------------------------------

export interface PublicMaintenance {
  active: boolean;
  message: string;
  endsAt?: string;
  upcoming?: { startsAt: string; endsAt: string };
}

export const publicMaintenanceQuery = queryOptions({
  queryKey: ['config', 'maintenance'] as const,
  queryFn: () => call<PublicMaintenance>(() => api.api.maintenance.$get()),
  staleTime: 30_000,
});
